import { FilterPriorityOrder, ProductConditionKey, ProductType, UnifiedTransitions } from './enums';
import { createImageVariantConfig } from './sdkLoader';

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

/**
 * Returns a capitalized string.
 * @param {string} str - The string to capitalize.
 * @returns {string|null} The capitalized string, or null if input is invalid.
 */
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return null;
  }

  return (str.charAt(0).toUpperCase() + str.slice(1)).trim();
};
export const bytesToMB = bytes => {
  if (!bytes || isNaN(bytes)) {
    return 'N/A'; // Return an empty string or a placeholder if bytes is invalid
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Helper to generate a random password
export const generateRandomPassword = (length = 12) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
  let password = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};

// Helper to strip the extension from a file name
export const getBaseName = (fullName = '') => {
  // If there's no dot, return the full name. Otherwise, remove everything from the last dot onward.
  return fullName.replace(/\.[^/.]+$/, '');
};
export const extractFileExtension = filename => {
  // Check if file is valid and has a 'name' property
  if (!filename) {
    return null; // Return null if file or file.name is not defined
  }

  // Extract and return the file extension
  return filename.substring(filename.lastIndexOf('.') + 1).toLowerCase();
};

/**
 * Converts a Unix timestamp (in seconds) to a JavaScript Date object.
 *
 * @param {number} timestamp - Unix timestamp in seconds.
 * @returns {Date|null} A Date object if valid, else null.
 */
export const convertUnixTimestamp = timestamp => {
  if (timestamp == null) return null;
  return new Date(timestamp * 1000);
};

// Helpers to format / parse
export const formatNumberWithCommas = value => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return isNaN(num) ? '' : '£ ' + num.toLocaleString('en-GB');
};

export const parseNumberFromFormatted = value => value.replace(/\D/g, '');

export const deduplicateAndSplitColumns = columns => {
  const seen = new Set();

  // Flatten and deduplicate by label
  const deduped = columns.flat().filter(item => {
    if (seen.has(item.label)) return false;
    seen.add(item.label);
    return true;
  });

  // Split the array into two nearly equal parts
  const midpoint = Math.ceil(deduped.length / 2);
  return [deduped.slice(0, midpoint), deduped.slice(midpoint)];
};

export const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

export const hasCurrentUser = currentUser => {
  if (!currentUser || !currentUser?.id) {
    return false;
  }
  return true;
};

export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    // Panel width relative to the viewport
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

export const formatListingData = values => {
  const {
    images,
    title,
    price,
    shippingPriceInSubunitsAdditionalItems,
    shippingPriceInSubunitsOneItem,
    deliveryOptions,
    stock,
    description,
    ...rest
  } = values || {};

  const shippingDataMaybe = {
    shippingPriceInSubunitsOneItem: shippingPriceInSubunitsOneItem?.amount,
    shippingPriceInSubunitsAdditionalItems: shippingPriceInSubunitsAdditionalItems?.amount,
  };

  const shippingEnabled = Array.isArray(deliveryOptions) && deliveryOptions.includes(SHIPPING);

  let updatedValues = {
    title: title || null, // Keep title intact
    description: description || '', // Add description at the root level
    price: price || null,
    stockUpdate: {
      oldTotal: null, // Assuming no current stock info is provided
      newTotal: stock,
    },
    publicData: {
      ...rest,
    },
  };

  if (shippingEnabled) {
    Object.assign(updatedValues.publicData, {
      ...shippingDataMaybe,
      shippingEnabled,
      deliveryOptions,
    });
  }

  // Remove null or undefined values from updatedValues
  const cleanData = obj =>
    Object.entries(obj)
      .filter(([_, v]) => v != null) // Remove keys with null or undefined values
      .reduce(
        (acc, [k, v]) => ({
          ...acc,
          [k]: typeof v === 'object' && !Array.isArray(v) ? cleanData(v) : v,
        }),
        {}
      );

  updatedValues = cleanData(updatedValues);

  // Exclude images from the final result
  delete updatedValues.images;
  delete updatedValues.stockUpdate;

  return updatedValues;
};

export const getProductConditions = (type, options) => {
  if (type === ProductType.NEW) {
    return options.filter(
      item =>
        item.key === ProductConditionKey.NEW_TAGS || item.key === ProductConditionKey.WITHOUT_TAGS
    );
  } else {
    return options.filter(
      item =>
        item.key === ProductConditionKey.VERY_GOOD ||
        item.key === ProductConditionKey.GOOD ||
        item.key === ProductConditionKey.SATISFACTORY
    );
  }
};

export const groupListingsByAuthor = listings => {
  return listings.reduce((grouped, listing) => {
    const authorId = listing.author.id.uuid;
    if (!grouped[authorId]) {
      grouped[authorId] = [];
    }
    grouped[authorId].push(listing);
    return grouped;
  }, {});
};

export const getFilterPriority = filterKey => {
  return FilterPriorityOrder[filterKey] || 1000; // Default priority for unlisted filters
};

export const normalizeParams = (shop, userId) => {
  if (!shop || !userId || typeof shop !== 'string' || typeof userId !== 'string') {
    throw new Error('Valid shop and userId strings are required');
  }
  const normalizedShop = shop
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');

  return { shop: normalizedShop, userId };
};

export const currencyToGBP = (amount, currencyCode) => {
  const rates = {
    GBP: 1,
    USD: 0.73,
    EUR: 0.87,
    INR: 0.0083,
    CAD: 0.53,
    AUD: 0.52,
    SGD: 0.58,
    AED: 0.21,
    JPY: 0.005,
    CNY: 0.11,
  };

  const rate = rates[currencyCode] ?? 1; // fallback to 1 if unknown currency
  return {
    convertedAmount: parseFloat(amount) * rate,
    convertedCurrency: 'GBP',
  };
};

export const getCurrency = config => {
  const curr = config?.currency === 'GBP' ? '£' : '$';
  return curr;
};

export function getPublicDataLabels(publicData, config) {
  const result = {};

  // 1. Get category label from categoryConfiguration
  const categoryId = publicData.category;
  const category = config?.categoryConfiguration?.categories?.find(cat => cat.id === categoryId);
  if (category) {
    result.category = category.name;
  }

  if (publicData?.size) {
    result.size = publicData?.size;
  }
  if (publicData?.condition) {
    result.condition = toCamelCaseLabel(publicData.condition);
  }

  // 2. Loop through listingFields and extract label from enumOptions
  const listingFields = config?.listing?.listingFields || [];
  listingFields.forEach(field => {
    const key = `pub_${field.key}`; // publicData keys are usually prefixed with 'pub_'
    const value = publicData[key];
    if (value) {
      // Handle single or multiple values
      const values = Array.isArray(value) ? value : [value];

      const labels = values
        .map(val => {
          const match = field.enumOptions?.find(opt => opt.option === val);
          return match?.label || val;
        })
        .filter(Boolean);

      result[field.key] = labels.length === 1 ? labels[0] : labels;
    }
  });

  return result;
}

// Generic helper function to get labels from enum options
export const getLabelsFromOptions = (fieldKey, values, listingFields) => {
  if (!values || !Array.isArray(values) || values.length === 0) {
    return null;
  }

  const fieldConfig = listingFields?.find(field => field?.key === fieldKey);
  const enumOptions = fieldConfig?.enumOptions || [];

  // Map values to labels
  const mappedLabels = values
    .map(value => {
      const option = enumOptions.find(opt => opt.option === value);
      return option ? option.label : null;
    })
    .filter(Boolean); // Remove null values

  return mappedLabels.length > 0 ? mappedLabels.join(', ') : null;
};

// Canonical sets for the productcondition mapping
export const NEW_SET = ['newtags', 'withouttags'];
export const USED_SET = ['vgood', 'good', 'satisfactory'];

/** Normalize incoming key to compare safely */
const normalizeKey = key =>
  String(key || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');

/**
 * Return the condition category for a given key.
 * @param {string} key
 * @returns {'new' | 'used' | 'unknown'}
 */
export const getConditionFromKey = key => {
  const k = normalizeKey(key);
  if (NEW_SET.includes(k)) return 'new';
  if (USED_SET.includes(k)) return 'used';
  return 'unknown';
};

/**
 * Convenience boolean: is this "new"?
 * @param {string} key
 * @returns {boolean}
 */
export const isNewCondition = key => getConditionFromKey(key) === 'new';

export const formatDateShort = (dateInput, fallback = '') => {
  if (!dateInput) return fallback;

  try {
    const date = new Date(dateInput);

    // Check if date is valid
    if (isNaN(date.getTime())) return fallback;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    console.warn('Invalid date input:', dateInput);
    return fallback;
  }
};

export const formatDateTime = (dateInput, fallback = '') => {
  if (!dateInput) return fallback;
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return fallback;

    const datePart = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return `${datePart}, ${timePart}`;
  } catch {
    return fallback;
  }
};

export const getListingImage = listing => {
  if (!listing || !listing.images || !Array.isArray(listing.images)) {
    return null;
  }

  // Get the first image (you can change to another index if needed)
  const firstImage = listing.images[0];
  const variants = firstImage?.attributes?.variants;

  // Pick the largest available variant (prefers 2x if available)
  const imageUrl = variants?.['square-small-2x']?.url || variants?.['square-small']?.url || null;

  return imageUrl;
};

export const formatTimeAgo = dateString => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  if (diffInSeconds < 604800) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 604800), 'week');
  return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
};

export const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

export const formatLineItemUnitType = (transaction, listing) => {
  // unitType should always be saved to transaction's protected data
  const unitTypeInProtectedData = transaction?.attributes?.protectedData?.unitType;
  // If unitType is not found (old or mutated data), we check listing's publicData
  // Note: this might have changed over time
  const unitTypeInListingPublicData = listing?.attributes?.publicData?.unitType;
  return `line-item/${unitTypeInProtectedData || unitTypeInListingPublicData}`;
};

export const roleInTx = (tx, currentUserId) =>
  tx.customer.id.uuid === currentUserId ? 'customer' : 'provider';

const PAYMENT_CONFIRMED_TITLE = {
  provider: 'Payment confirmed — new order received',
  customer: 'Payment confirmed',
};

export const NOTIF_TITLES = {
  // -----------------------
  // Offers / bargaining
  // -----------------------
  'transition/send-offer': {
    provider: 'You received a new offer',
    customer: 'Your offer was sent',
  },
  'transition/expire-offer': {
    provider: 'Offer expired',
    customer: 'Your offer expired',
  },
  'transition/accept-offer': {
    provider: 'You accepted the offer',
    customer: 'Your offer was accepted',
  },
  'transition/decline-offer': {
    provider: 'You declined the offer',
    customer: 'Your offer was declined',
  },
  'transition/counter-offer': {
    customer: 'You received a counter-offer',
    provider: 'You sent a counter-offer',
  },
  'transition/counter-after-counter-1': {
    provider: 'Buyer countered again',
    customer: 'You sent another counter-offer',
  },
  'transition/counter-after-counter-2': {
    customer: 'Seller countered again',
    provider: 'You sent another counter-offer',
  },
  'transition/accept-after-counter-1': {
    provider: 'Buyer accepted your counter-offer',
    customer: 'You accepted the counter-offer',
  },
  'transition/decline-after-counter-1': {
    provider: 'Buyer declined your counter-offer',
    customer: 'You declined the counter-offer',
  },
  'transition/accept-after-counter-2': {
    customer: 'Seller accepted your counter-offer',
    provider: 'You accepted the counter-offer',
  },
  'transition/decline-after-counter-2': {
    customer: 'Seller declined your counter-offer',
    provider: 'You declined the counter-offer',
  },
  'transition/accept-after-counter-3': {
    provider: 'Buyer accepted the final counter-offer',
    customer: 'You accepted the final counter-offer',
  },
  'transition/decline-after-counter-3': {
    provider: 'Buyer declined the final counter-offer',
    customer: 'You declined the final counter-offer',
  },

  // -----------------------
  // Inquiry
  // -----------------------
  'transition/inquire': {
    provider: 'New inquiry received',
    customer: 'Inquiry sent',
  },

  // -----------------------
  // Payment (Stripe)
  // -----------------------
  'transition/request-payment': PAYMENT_CONFIRMED_TITLE,
  'transition/request-payment-after-inquiry': PAYMENT_CONFIRMED_TITLE,
  'transition/request-payment-after-offer-accepted': PAYMENT_CONFIRMED_TITLE,
  'transition/confirm-payment': PAYMENT_CONFIRMED_TITLE,
  'transition/expire-payment': {
    provider: 'Payment expired',
    customer: 'Payment expired',
  },

  // -----------------------
  // Payment (PayPal)
  // -----------------------
  'transition/request-payment-with-paypal': PAYMENT_CONFIRMED_TITLE,
  'transition/request-payment-after-enquiry-with-paypal': PAYMENT_CONFIRMED_TITLE,
  'transition/confirm-payment-with-paypal': PAYMENT_CONFIRMED_TITLE,
  'transition/expire-payment-with-paypal': {
    provider: 'Payment expired',
    customer: 'Payment expired',
  },

  // -----------------------
  // Payment (Wallet)
  // -----------------------
  'transition/request-payment-with-wallet': PAYMENT_CONFIRMED_TITLE,
  'transition/request-payment-after-enquiry-with-wallet': PAYMENT_CONFIRMED_TITLE,
  'transition/confirm-payment-with-wallet': PAYMENT_CONFIRMED_TITLE,
  'transition/expire-payment-with-wallet': {
    provider: 'Payment expired',
    customer: 'Payment expired',
  },

  // -----------------------
  // Purchased → Shipped / Received (Stripe)
  // -----------------------
  'transition/mark-delivered': {
    customer: 'Your order has been shipped',
    provider: 'You marked the order as shipped',
  },
  'transition/operator-mark-delivered': {
    customer: 'Your order has been shipped',
    provider: 'Order marked as shipped by operator',
  },
  'transition/mark-received': {
    provider: 'Buyer confirmed they received the order',
    customer: 'You confirmed you received the order',
  },
  'transition/operator-mark-received': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },
  'transition/auto-mark-received': {
    provider: 'Order was auto-marked as received',
    customer: 'Order was auto-marked as received',
  },
  'transition/mark-received-from-purchased': {
    provider: 'Buyer marked the order as received',
    customer: 'You marked the order as received',
  },
  'transition/mark-received-from-purchased-operator': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },

  // -----------------------
  // Dispute (Stripe)
  // -----------------------
  'transition/dispute': {
    provider: 'Order disputed',
    customer: 'You opened a dispute',
  },
  'transition/operator-dispute': {
    provider: 'Order disputed by operator',
    customer: 'Dispute opened by operator',
  },
  'transition/mark-received-from-disputed': {
    provider: 'Order marked as received after dispute',
    customer: 'Order was marked as received after dispute',
  },
  'transition/cancel-from-disputed': {
    provider: 'Order canceled after dispute',
    customer: 'Your order was canceled after dispute',
  },
  'transition/auto-cancel-from-disputed': {
    provider: 'Order auto-canceled after dispute',
    customer: 'Order auto-canceled after dispute',
  },
  'transition/evidence-exchange-by-seller': {
    customer: 'Seller added evidence',
    provider: 'You added evidence',
  },
  'transition/evidence-exchange-by-seller-again': {
    customer: 'Seller added more evidence',
    provider: 'You added more evidence',
  },
  'transition/evidence-exchange-by-buyer': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/evidence-exchange-by-buyer-again': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/escalate-by-seller': {
    customer: 'Seller escalated the dispute',
    provider: 'You escalated the dispute',
  },
  'transition/escalate-by-buyer': {
    provider: 'Buyer escalated the dispute',
    customer: 'You escalated the dispute',
  },
  'transition/refund-issued-return-required': {
    provider: 'Refund issued (return required)',
    customer: 'Refund issued (return required)',
  },
  'transition/return-received': {
    provider: 'Return received',
    customer: 'Return received',
  },
  'transition/problem-with-returned-item': {
    provider: 'Problem reported with returned item',
    customer: 'Seller reported a problem with the returned item',
  },
  'transition/final-review-and-refund': {
    provider: 'Final review complete — refunded',
    customer: 'Final review complete — refunded',
  },
  'transition/final-review-and-fund-released': {
    provider: 'Final review complete — funds released',
    customer: 'Final review complete — funds released',
  },
  'transition/refund-without-return': {
    provider: 'Refund issued (no return required)',
    customer: 'Refund issued (no return required)',
  },
  'transition/funds-released': {
    provider: 'Funds released',
    customer: 'Funds released',
  },

  // -----------------------
  // Purchased → Shipped / Received (PayPal)
  // -----------------------
  'transition/mark-delivered-with-paypal': {
    customer: 'Your order has been shipped',
    provider: 'You marked the order as shipped',
  },
  'transition/mark-delivered-with-paypal-operator': {
    customer: 'Your order has been shipped',
    provider: 'Order marked as shipped by operator',
  },
  'transition/mark-received-with-paypal': {
    provider: 'Buyer confirmed they received the order',
    customer: 'You confirmed you received the order',
  },
  'transition/mark-received-with-paypal-operator': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },
  'transition/auto-mark-received-with-paypal': {
    provider: 'Order was auto-marked as received',
    customer: 'Order was auto-marked as received',
  },
  'transition/mark-received-from-purchased-with-paypal': {
    provider: 'Buyer marked the order as received',
    customer: 'You marked the order as received',
  },
  'transition/mark-received-from-purchased-with-paypal-operator': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },

  // -----------------------
  // Dispute (PayPal)
  // -----------------------
  'transition/dispute-with-paypal': {
    provider: 'Order disputed',
    customer: 'You opened a dispute',
  },
  'transition/operator-dispute-with-paypal': {
    provider: 'Order disputed by operator',
    customer: 'Dispute opened by operator',
  },
  'transition/mark-received-from-disputed-with-paypal': {
    provider: 'Order marked as received after dispute',
    customer: 'Order was marked as received after dispute',
  },
  'transition/cancel-from-disputed-with-paypal': {
    provider: 'Order canceled after dispute',
    customer: 'Your order was canceled after dispute',
  },
  'transition/auto-cancel-from-disputed-with-paypal': {
    provider: 'Order auto-canceled after dispute',
    customer: 'Order auto-canceled after dispute',
  },
  'transition/evidence-exchange-by-seller-with-paypal': {
    customer: 'Seller added evidence',
    provider: 'You added evidence',
  },
  'transition/evidence-exchange-by-seller-again-with-paypal': {
    customer: 'Seller added more evidence',
    provider: 'You added more evidence',
  },
  'transition/evidence-exchange-by-buyer-with-paypal': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/evidence-exchange-by-again-with-paypal': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/escalate-by-seller-with-paypal': {
    customer: 'Seller escalated the dispute',
    provider: 'You escalated the dispute',
  },
  'transition/escalate-by-buyer-with-paypal': {
    provider: 'Buyer escalated the dispute',
    customer: 'You escalated the dispute',
  },
  'transition/refund-issued-return-required-with-paypal': {
    provider: 'Refund issued (return required)',
    customer: 'Refund issued (return required)',
  },
  'transition/return-received-with-paypal': {
    provider: 'Return received',
    customer: 'Return received',
  },
  'transition/problem-with-returned-item-with-paypal': {
    provider: 'Problem reported with returned item',
    customer: 'Seller reported a problem with the returned item',
  },
  'transition/final-review-and-refund-with-paypal': {
    provider: 'Final review complete — refunded',
    customer: 'Final review complete — refunded',
  },
  'transition/final-review-and-fund-released-with-paypal': {
    provider: 'Final review complete — funds released',
    customer: 'Final review complete — funds released',
  },
  'transition/refund-without-return-with-paypal': {
    provider: 'Refund issued (no return required)',
    customer: 'Refund issued (no return required)',
  },
  'transition/funds-released-with-paypal': {
    provider: 'Funds released',
    customer: 'Funds released',
  },
  'transition/cancel-with-paypal': {
    provider: 'Order canceled',
    customer: 'Your order was canceled',
  },

  // -----------------------
  // Purchased → Shipped / Received (Wallet)
  // -----------------------
  'transition/mark-delivered-with-wallet': {
    customer: 'Your order has been shipped',
    provider: 'You marked the order as shipped',
  },
  'transition/mark-delivered-with-wallet-operator': {
    customer: 'Your order has been shipped',
    provider: 'Order marked as shipped by operator',
  },
  'transition/mark-received-with-wallet': {
    provider: 'Buyer confirmed they received the order',
    customer: 'You confirmed you received the order',
  },
  'transition/mark-received-with-wallet-operator': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },
  'transition/auto-mark-received-with-wallet': {
    provider: 'Order was auto-marked as received',
    customer: 'Order was auto-marked as received',
  },
  'transition/mark-received-from-purchased-with-wallet': {
    provider: 'Buyer marked the order as received',
    customer: 'You marked the order as received',
  },
  'transition/mark-received-from-purchased-with-wallet-operator': {
    provider: 'Order was marked as received after carrier delivery',
    customer: 'Your order was marked as received after carrier delivery',
  },

  // -----------------------
  // Dispute (Wallet)
  // -----------------------
  'transition/dispute-with-wallet': {
    provider: 'Order disputed',
    customer: 'You opened a dispute',
  },
  'transition/operator-dispute-with-wallet': {
    provider: 'Order disputed by operator',
    customer: 'Dispute opened by operator',
  },
  'transition/mark-received-from-disputed-with-wallet': {
    provider: 'Order marked as received after dispute',
    customer: 'Order was marked as received after dispute',
  },
  'transition/cancel-from-disputed-with-wallet': {
    provider: 'Order canceled after dispute',
    customer: 'Your order was canceled after dispute',
  },
  'transition/auto-cancel-from-disputed-with-wallet': {
    provider: 'Order auto-canceled after dispute',
    customer: 'Order auto-canceled after dispute',
  },
  'transition/evidence-exchange-by-seller-with-wallet': {
    customer: 'Seller added evidence',
    provider: 'You added evidence',
  },
  'transition/evidence-exchange-by-seller-again-with-wallet': {
    customer: 'Seller added more evidence',
    provider: 'You added more evidence',
  },
  'transition/evidence-exchange-by-buyer-again-with-wallet': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/evidence-exchange-by-buyer-with-wallet': {
    provider: 'Buyer added evidence',
    customer: 'You added evidence',
  },
  'transition/escalate-by-seller-with-wallet': {
    customer: 'Seller escalated the dispute',
    provider: 'You escalated the dispute',
  },
  'transition/escalate-by-buyer-with-wallet': {
    provider: 'Buyer escalated the dispute',
    customer: 'You escalated the dispute',
  },
  'transition/refund-issued-return-required-with-wallet': {
    provider: 'Refund issued (return required)',
    customer: 'Refund issued (return required)',
  },
  'transition/return-received-with-wallet': {
    provider: 'Return received',
    customer: 'Return received',
  },
  'transition/problem-with-returned-item-with-wallet': {
    provider: 'Problem reported with returned item',
    customer: 'Seller reported a problem with the returned item',
  },
  'transition/final-review-and-refund-with-wallet': {
    provider: 'Final review complete — refunded',
    customer: 'Final review complete — refunded',
  },
  'transition/final-review-and-fund-released-with-wallet': {
    provider: 'Final review complete — funds released',
    customer: 'Final review complete — funds released',
  },
  'transition/refund-without-return-with-wallet': {
    provider: 'Refund issued (no return required)',
    customer: 'Refund issued (no return required)',
  },
  'transition/funds-released-with-wallet': {
    provider: 'Funds released',
    customer: 'Funds released',
  },
  'transition/cancel-with-wallet': {
    provider: 'Order canceled',
    customer: 'Your order was canceled',
  },

  // -----------------------
  // Cancel / Complete / Reviews
  // -----------------------
  'transition/cancel': {
    provider: 'Order canceled',
    customer: 'Your order was canceled',
  },
  'transition/auto-cancel': {
    provider: 'Order auto-canceled',
    customer: 'Order auto-canceled',
  },
  'transition/auto-cancel-with-paypal': {
    provider: 'Order auto-canceled',
    customer: 'Order auto-canceled',
  },
  'transition/auto-cancel-with-wallet': {
    provider: 'Order auto-canceled',
    customer: 'Order auto-canceled',
  },
  'transition/auto-complete': {
    provider: 'Order completed',
    customer: 'Order completed',
  },

  // Reviews
  'transition/review-1-by-provider': {
    provider: 'You left a review',
    customer: 'You received a review',
  },
  'transition/review-2-by-provider': {
    provider: 'You left a review',
    customer: 'You received a review',
  },
  'transition/review-1-by-customer': {
    customer: 'You left a review',
    provider: 'You received a review',
  },
  'transition/review-2-by-customer': {
    customer: 'You left a review',
    provider: 'You received a review',
  },
  'transition/expire-review-period': {
    provider: 'Review period ended',
    customer: 'Review period ended',
  },
  'transition/expire-provider-review-period': {
    provider: 'Review period ended',
    customer: 'Review period ended',
  },
  'transition/expire-customer-review-period': {
    provider: 'Review period ended',
    customer: 'Review period ended',
  },
};

const humanizeTransition = s =>
  (s || '')
    .replace('transition/', '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

export const getNotifTitle = (transitionName, myRole) => {
  const entry = NOTIF_TITLES[transitionName];
  if (!entry) return humanizeTransition(transitionName);
  return entry[myRole] || entry.customer || entry.provider || humanizeTransition(transitionName);
};
export const getMessageNotifTitle = (actionBy, myRole) => {
  return actionBy === myRole ? 'You sent a message' : 'New message';
};

export const getAnyNotifTitle = (notif, myRole) => {
  if (notif?.type === 'message') {
    return getMessageNotifTitle(notif.actionBy, myRole);
  }
  // fallback to your existing transition title map
  return getNotifTitle(notif?.transitionName, myRole);
};

export const getAnyNotifBody = (notif, myRole, listingTitle) => {
  if (!notif) return '';
  const safeTitle = listingTitle || 'this order';

  const { type, actionBy, transitionName: t } = notif;
  const iAmActor = actionBy && myRole && actionBy === myRole;

  // Messages
  if (type === 'message') {
    return iAmActor
      ? `You sent a new message about "${safeTitle}".`
      : `You received a new message about "${safeTitle}".`;
  }

  // Order / offer transitions
  switch (t) {
    case 'transition/confirm-payment':
    case 'transition/confirm-payment-with-paypal':
    case 'transition/confirm-payment-with-wallet':
      return myRole === 'customer'
        ? `Your payment for "${safeTitle}" is confirmed.`
        : `You received a new paid order for "${safeTitle}".`;

    case 'transition/request-payment':
    case 'transition/request-payment-with-wallet':
    case 'transition/request-payment-with-paypal':
    case 'transition/request-payment-after-offer-accepted':
      return myRole === 'customer'
        ? `You requested payment for "${safeTitle}".`
        : `Buyer wants to proceed with payment for "${safeTitle}".`;

    case 'transition/shipped':
      return myRole === 'customer'
        ? `Your order for "${safeTitle}" is on its way.`
        : `You marked the order for "${safeTitle}" as shipped.`;

    case 'transition/delivered':
    case 'transition/mark-received':
    case 'transition/mark-received-with-paypal':
    case 'transition/mark-received-with-wallet':
    case 'transition/mark-received-from-purchased-with-paypal':
    case 'transition/mark-received-from-purchased-with-wallet':
    case 'transition/mark-received-from-purchased':
      return myRole === 'customer'
        ? `You confirmed receiving "${safeTitle}".`
        : `Buyer confirmed receiving "${safeTitle}".`;

    // --- OFFERS: use actionBy vs myRole correctly ---
    case 'transition/send-offer':
    case 'transition/counter-offer':
    case 'transition/counter-after-counter-1':
    case 'transition/counter-after-counter-2':
      return iAmActor
        ? `You sent a new offer for "${safeTitle}".`
        : `You received a new offer for "${safeTitle}".`;

    case 'transition/accept-offer':
    case 'transition/accept-after-counter-1':
    case 'transition/accept-after-counter-2':
    case 'transition/accept-after-counter-3':
      return iAmActor
        ? `You accepted the offer for "${safeTitle}".`
        : `Your offer for "${safeTitle}" was accepted.`;

    case 'transition/decline-offer':
    case 'transition/decline-after-counter-1':
    case 'transition/decline-after-counter-2':
    case 'transition/decline-after-counter-3':
      return iAmActor
        ? `You declined the offer for "${safeTitle}".`
        : `Your offer for "${safeTitle}" was declined.`;

    default:
      return `See the latest update on your order for "${safeTitle}".`;
  }
};

// createdAt may be unix (ms) or ISO; normalize to ms
export const toMs = v => (typeof v === 'number' ? v : Date.parse(v || 0)) || 0;

// Flatten all tx.notificationData into one list for current user
export const buildNotifications = (transactions, currentUser) => {
  const me = currentUser?.id?.uuid;
  if (!me) return [];

  const flattened = [];

  transactions.forEach(tx => {
    const myRole = roleInTx(tx, me); // 'customer' or 'provider'
    const md = tx?.attributes?.metadata || {};
    const list = Array.isArray(md.notificationData) ? md.notificationData : [];

    // only include entries where I am the target
    list
      .filter(n => n?.notifyTo === myRole)
      .forEach(n => {
        const createdAtMs = toMs(n.createdAt);
        const seen = Boolean(n?.seenBy?.[me]);
        const otherUser = myRole === 'customer' ? tx.provider : tx.customer;

        flattened.push({
          ...n,
          tx,
          txId: tx.id.uuid,
          createdAtMs,
          unread: !seen, // unread means: I am target && not seen
          otherUser,
        });
      });
  });

  // newest first
  flattened.sort((a, b) => b.createdAtMs - a.createdAtMs);
  return flattened;
};

export const computeNotifTs = notif => {
  if (!notif) return 0;

  const now = Date.now();

  // 1) Prefer createdAtMs
  if (notif.createdAtMs != null) {
    if (typeof notif.createdAtMs === 'number' && Number.isFinite(notif.createdAtMs)) {
      // number, could be seconds or ms
      return notif.createdAtMs < 1e12 ? notif.createdAtMs * 1000 : notif.createdAtMs;
    }
    if (typeof notif.createdAtMs === 'string') {
      const ms = Date.parse(notif.createdAtMs);
      if (Number.isFinite(ms)) return ms;
    }
  }

  // 2) Fallback to createdAt
  if (notif.createdAt != null) {
    if (typeof notif.createdAt === 'number' && Number.isFinite(notif.createdAt)) {
      return notif.createdAt < 1e12 ? notif.createdAt * 1000 : notif.createdAt;
    }
    if (typeof notif.createdAt === 'string') {
      const ms = Date.parse(notif.createdAt);
      if (Number.isFinite(ms)) return ms;
    }
  }

  // 3) Optional fallback from transaction itself
  const tx = notif.tx;
  const txCreated =
    tx?.attributes?.lastTransitionedAt || tx?.attributes?.createdAt || tx?.attributes?.updatedAt;

  if (txCreated) {
    const ms = Date.parse(txCreated);
    if (Number.isFinite(ms)) return ms;
  }

  // 4) Last resort: treat as "now" so it doesn't sink to the bottom
  return now;
};

export const getDisputeDetailsFromTx = tx => {
  if (!tx) return null;

  const protectedData = tx.protectedData || tx.attributes?.protectedData || {};

  const {
    disputeDetails: {
      issueType = '',
      subIssueType = '',
      disputeReason = '',
      damageImages = [],
    } = {},
  } = protectedData || {};

  const hasAnyData =
    issueType ||
    subIssueType ||
    disputeReason ||
    (Array.isArray(damageImages) && damageImages.length > 0);

  if (!hasAnyData) {
    return null;
  }

  return {
    issueType,
    subIssueType,
    disputeReason,
    damageImages: Array.isArray(damageImages) ? damageImages : [],
  };
};

/**
 * Turn codes like "item_not_as_described" into "Item Not As Described"
 */
export const formatCodeToLabel = value => {
  if (!value) return '';
  return value
    .split(/[_-]/g)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const getUnified = (group, transitionMap) => group.map(t => transitionMap[t]).find(Boolean);

const transitionVariantFromLast = lastTransition => {
  const lt = String(lastTransition || '');
  if (lt.includes('paypal')) return 'paypal';
  if (lt.includes('wallet')) return 'wallet';
  return 'default';
};

const orderCandidatesByVariant = (candidates, variant) => {
  const isPaypal = t => String(t).includes('paypal');
  const isWallet = t => String(t).includes('wallet');

  if (variant === 'paypal') {
    return [...candidates.filter(isPaypal), ...candidates.filter(t => !isPaypal(t))];
  }
  if (variant === 'wallet') {
    return [...candidates.filter(isWallet), ...candidates.filter(t => !isWallet(t))];
  }
  return [
    ...candidates.filter(t => !isPaypal(t) && !isWallet(t)),
    ...candidates.filter(t => isPaypal(t) || isWallet(t)),
  ];
};

const isEvidence = t => String(t || '').includes('transition/evidence-exchange-by-');

const actorEvidenceFilter = (candidates, actorRole, lastTransition) => {
  const isBuyerAgain = t => String(t || '').includes('by-buyer-again');
  const isBuyer = t =>
    String(t || '').includes('by-buyer') && !String(t || '').includes('by-buyer-again');

  const isSellerAgain = t => String(t || '').includes('by-seller-again');
  const isSeller = t =>
    String(t || '').includes('by-seller') && !String(t || '').includes('by-seller-again');

  const lastWasEvidence =
    isEvidence(lastTransition) || String(lastTransition || '').includes('evidence-exchange');

  // Customer (buyer) path: first evidence vs "again"
  if (actorRole === 'customer') {
    return lastWasEvidence ? candidates.filter(isBuyerAgain) : candidates.filter(isBuyer);
  }

  // Provider (seller) path: first evidence vs "again" (unchanged logic, just clearer)
  return lastWasEvidence ? candidates.filter(isSellerAgain) : candidates.filter(isSeller);
};

export const getValidUnifiedTransition = (process, unifiedKey, lastTransition, actorRole) => {
  const candidatesRaw = UnifiedTransitions?.[unifiedKey] || [];
  if (!candidatesRaw.length) return null;

  let candidates =
    unifiedKey === 'EVIDENCE_EXCHANGE'
      ? actorEvidenceFilter(candidatesRaw, actorRole, lastTransition)
      : candidatesRaw;

  const variant = transitionVariantFromLast(lastTransition);
  candidates = orderCandidatesByVariant(candidates, variant);

  const available = process?.transitions ? Object.values(process.transitions) : [];
  return candidates.find(t => available.includes(t)) || null;
};

export const buildTitlePriceArray = (cartItems = []) => {
  if (!Array.isArray(cartItems)) return [];

  return cartItems.map(item => {
    const amount = item?.price?.amount || 0;
    const stockCount = item?.stockCount || 0;
    const currency = item?.price?.currency || 'GBP';

    const totalInSubunits = amount * stockCount;
    const totalInUnits = totalInSubunits / 100;

    return {
      listTitle: item?.title || '',
      totalPrice: `${totalInUnits.toFixed(2)} ${currency}`,
    };
  });
};

/**
 * Removes inquiry- and offer-related transitions from a transitions array
 *
 * @param {string[]} transitions
 * @returns {string[]}
 */
export function trimInquiryTransitions(transitions = []) {
  if (!Array.isArray(transitions)) return [];

  const BLOCKED_KEYWORDS = [
    // inquiry
    'inquire',
    'inquiry',
    'enquiry',

    // offers
    'offer',
    'counter',
    'accept-offer',
    'decline-offer',
    'expire-offer',
    'accept-after-counter',
    'decline-after-counter',
    'counter-after-counter',
  ];

  return transitions.filter(
    transition => !BLOCKED_KEYWORDS.some(keyword => transition.includes(keyword))
  );
}

/**
 * Removes all payment-related transitions
 *
 * @param {string[]} transitions
 * @returns {string[]}
 */
export const removePaymentTransitions = (transitions = []) => {
  if (!Array.isArray(transitions)) return [];

  const PAYMENT_KEYWORDS = ['payment', 'request-payment', 'expire-payment'];

  return transitions.filter(
    transition => !PAYMENT_KEYWORDS.some(keyword => transition.includes(keyword))
  );
};
